import { useCallback, useState } from "react";
import type { Coupon } from "@/apis/coupon/coupon-apis";
import { useCoupon } from "@/v2/queries";
import {
  type CreateModeEntityModalResult,
  createEntityModalHook,
} from "./createEntityModalHook";

const useBaseCouponModal = createEntityModalHook<Coupon, number>({
  useQueryHook: useCoupon,
  getEntityId: (coupon) => coupon.id,
  supportsCreateMode: true,
});

export interface UseCouponModalResult
  extends Omit<CreateModeEntityModalResult<Coupon, number>, "entity" | "open"> {
  /** The coupon being viewed/edited (null in create mode) */
  coupon: Coupon | null;
  /** Whether the modal is in edit mode (vs view-only mode) */
  isEditMode: boolean;
  /** Open the modal for viewing or editing a specific coupon */
  open: (id: number, options?: { edit?: boolean }) => void;
}

/**
 * Hook for managing coupon modal state and data loading.
 *
 * Handles:
 * - Modal visibility state for create, view, and edit modes
 * - Loading coupon data via TanStack Query for view/edit mode
 * - Modal opens as soon as coupon loads (prevents stale cached data)
 */
export function useCouponModal(): UseCouponModalResult {
  const baseModal = useBaseCouponModal();
  const [isEditMode, setIsEditMode] = useState(false);

  const open = useCallback(
    (id: number, options?: { edit?: boolean }) => {
      setIsEditMode(options?.edit ?? false);
      baseModal.open(id);
    },
    [baseModal.open]
  );

  const close = useCallback(() => {
    setIsEditMode(false);
    baseModal.close();
  }, [baseModal.close]);

  const openCreate = useCallback(() => {
    setIsEditMode(false);
    baseModal.openCreate();
  }, [baseModal.openCreate]);

  return {
    isOpen: baseModal.isOpen,
    isCreateMode: baseModal.isCreateMode,
    isEditMode,
    coupon: baseModal.entity,
    isLoading: baseModal.isLoading,
    open,
    openCreate,
    close,
  };
}
